// frontend/src/pages/index.tsx
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from 'pages/Page';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

// ✅ Interface adaptée aux vraies données de l'API
interface Cabinet {
  id: string;
  name: string;
  subdomain: string;
  database_name: string;
  admin_email: string;
  app_name: string;
  status: string;
  deployment_status: string;
  metadata: {
    url?: string;
    error?: string;
    steps?: Record<string, string>;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

// ✅ Templates prédéfinis
const EMAIL_TEMPLATES = {
  update: {
    subject: "🚀 Nouvelle mise à jour SmartLawyer",
    body: `Bonjour,

Nous sommes heureux de vous annoncer une nouvelle mise à jour de SmartLawyer !

## 🎯 Nouveautés

- Nouvelle fonctionnalité X
- Amélioration de la performance Y
- Correction de bugs

## 📚 Documentation

Pour plus d'informations, consultez notre documentation : https://docs.smartlawyer.ai

Cordialement,
L'équipe SmartLawyer`
  },
  maintenance: {
    subject: "⚠️ Maintenance programmée - SmartLawyer",
    body: `Bonjour,

Nous vous informons qu'une maintenance est programmée sur la plateforme SmartLawyer.

## 📅 Date et heure
- Date : [À compléter]
- Durée estimée : [À compléter]

## ⚡ Impact
Pendant cette période, la plateforme sera temporairement indisponible.

Nous vous remercions de votre compréhension.

Cordialement,
L'équipe SmartLawyer`
  },
  news: {
    subject: "📰 Actualités SmartLawyer",
    body: `Bonjour,

Voici les dernières actualités de SmartLawyer !

## 📢 Annonces

[Votre message ici]

## 🔗 Liens utiles
- Support : support@smartlawyer.ai
- Documentation : https://docs.smartlawyer.ai

Cordialement,
L'équipe SmartLawyer`
  },
  custom: {
    subject: "",
    body: ""
  }
};

const AdminContent = () => {
  const { user, isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const [cabinets, setCabinets] = useState<Cabinet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // ✅ États pour la newsletter
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [newsletterResults, setNewsletterResults] = useState<{ cabinet: string; email: string; success: boolean; error?: string }[]>([]);
  const [newsletterSuccess, setNewsletterSuccess] = useState('');
  
  // ✅ États du formulaire newsletter
  const [selectedTemplate, setSelectedTemplate] = useState<keyof typeof EMAIL_TEMPLATES>('custom');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [selectedCabinets, setSelectedCabinets] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(true);

  useEffect(() => {
    if (!isReady) {
      console.log('Auth not ready yet...');
      return;
    }
  
    if (!isAuthenticated) {
      console.log('Not authenticated, redirecting to login...');
      navigate('/login');
      return;
    }
  
    console.log('Authenticated! Loading cabinets...');
  
    fetch(apiClient.buildEndpoint('/api/tenants/'))
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load cabinets');
        return r.json();
      })
      .then((data) => {
        console.log('Cabinets loaded:', data);
        const cabinetsArray = Array.isArray(data) ? data : (data.tenants || []);
        console.log('Setting cabinets:', cabinetsArray.length);
        setCabinets(cabinetsArray);
        
        // Sélectionner tous les cabinets déployés par défaut
        const deployedIds = cabinetsArray
          .filter((c: Cabinet) => c.deployment_status === 'deployed')
          .map((c: Cabinet) => c.id);
        setSelectedCabinets(deployedIds);
        
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading cabinets:', err);
        setError('Erreur lors du chargement des cabinets');
        setLoading(false);
      });
  }, [isAuthenticated, isReady, navigate]);

  // ✅ Gérer le changement de template
  const handleTemplateChange = (template: keyof typeof EMAIL_TEMPLATES) => {
    setSelectedTemplate(template);
    setEmailSubject(EMAIL_TEMPLATES[template].subject);
    setEmailBody(EMAIL_TEMPLATES[template].body);
  };

  // ✅ Gérer la sélection de tous les cabinets
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const deployedIds = cabinets
        .filter(c => c.deployment_status === 'deployed')
        .map(c => c.id);
      setSelectedCabinets(deployedIds);
    } else {
      setSelectedCabinets([]);
    }
  };

  // ✅ Gérer la sélection individuelle
  const handleToggleCabinet = (cabinetId: string) => {
    setSelectedCabinets(prev => {
      if (prev.includes(cabinetId)) {
        return prev.filter(id => id !== cabinetId);
      } else {
        return [...prev, cabinetId];
      }
    });
    setSelectAll(false);
  };

  // ✅ Envoyer la newsletter
  const handleSendNewsletter = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      setError('Le sujet et le message sont obligatoires');
      return;
    }

    if (selectedCabinets.length === 0) {
      setError('Veuillez sélectionner au moins un cabinet');
      return;
    }

    setSendingNewsletter(true);
    setNewsletterResults([]);
    setError('');
    setNewsletterSuccess('');


    try {
      const response = await fetch(
        apiClient.buildEndpoint('/api/tenants/send-newsletter'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subject: emailSubject,
            message: emailBody,
            cabinet_ids: selectedCabinets
          })
        }
      );

      const result = await response.json();

      if (response.ok) {
        setNewsletterResults(result.results || []);
        const successCount = result.results.filter((r: any) => r.success).length;
        setNewsletterSuccess(`✅ ${successCount} email(s) envoyé(s) avec succès sur ${result.results.length}`);
      } else {
        setError(result.detail || 'Erreur lors de l\'envoi');
      }
    } catch (err: any) {
      setError(`Erreur réseau: ${err.message}`);
    } finally {
      setSendingNewsletter(false);
    }
  };

  // ✅ Réinitialiser la modal
  const resetNewsletterModal = () => {
    setShowNewsletterModal(false);
    setNewsletterResults([]);
    setNewsletterSuccess('');
    setSelectedTemplate('custom');
    setEmailSubject('');
    setEmailBody('');
    const deployedIds = cabinets
      .filter(c => c.deployment_status === 'deployed')
      .map(c => c.id);
    setSelectedCabinets(deployedIds);
    setSelectAll(true);
  };

  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="!size-6" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  const getTenantUrl = (cabinet: Cabinet) => {
    return cabinet.metadata?.url || `https://${cabinet.subdomain}.smartlawyer.ai`;
  };

  const getDeploymentStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      deployed: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Déployé' },
      provisioning: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'En cours' },
      error: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Erreur' },
      pending: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'En attente' },
      deprovisioning: { color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', label: 'Suppression' },
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-700', label: status };
    return config;
  };

  const deployedCabinets = cabinets.filter(c => c.deployment_status === 'deployed');
  
  return (
    <div className="flex flex-col h-full w-full p-6 overflow-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">
          🎯 SmartLawyer Platform Admin
        </h1>
        <p className="text-muted-foreground">
          Gestion des cabinets et provisioning • Connecté: {user.identifier}
        </p>
      </div>

      {/* Actions */}
      <div className="mb-8 flex gap-3 flex-wrap">
        <button
          onClick={() => navigate('/admin/tenants/create')}
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
        >
          ➕ Créer un nouveau cabinet
        </button>
        
        {/* ✅ Bouton Newsletter */}
        <button
          onClick={() => setShowNewsletterModal(true)}
          disabled={deployedCabinets.length === 0}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={deployedCabinets.length === 0 ? "Aucun cabinet déployé" : "Envoyer un message aux administrateurs"}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          📧 Envoyer une newsletter ({deployedCabinets.length})
        </button>

        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          💬 Retour au chat
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            {error}
            <button 
              onClick={() => setError('')}
              className="ml-2 underline hover:no-underline"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Success Alert */}
      {newsletterSuccess && (
        <div className="mb-6 rounded-md border border-green-500 bg-green-50 dark:bg-green-950/50 p-4 text-sm text-green-700 dark:text-green-400 flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div>
            {newsletterSuccess}
            <button 
              onClick={() => setNewsletterSuccess('')}
              className="ml-2 underline hover:no-underline"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Cabinets List */}
      <div className="rounded-lg border bg-card flex-1">
        <div className="border-b p-4 bg-muted/50">
          <h2 className="text-lg font-semibold">
            Cabinets ({cabinets.length})
          </h2>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4">Chargement...</p>
            </div>
          ) : cabinets.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">📋</div>
              <p className="text-muted-foreground mb-4">
                Aucun cabinet pour le moment
              </p>
              <button
                onClick={() => navigate('/admin/tenants/create')}
                className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90"
              >
                Créer le premier cabinet
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {cabinets.map((cabinet) => {
                const statusBadge = getDeploymentStatusBadge(cabinet.deployment_status);
                
                return (
                  <div
                    key={cabinet.id}
                    onClick={() => navigate(`/admin/tenants/${cabinet.id}`)}
                    className="flex items-center justify-between rounded-md border p-4 hover:bg-accent cursor-pointer transition-colors group"
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold mb-1 truncate">
                        {cabinet.name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                        <span className="font-mono bg-muted px-2 py-0.5 rounded">
                          {cabinet.subdomain}
                        </span>
                        <span>•</span>
                        <span className="truncate">{cabinet.admin_email}</span>
                        <span>•</span>
                        <a
                          href={getTenantUrl(cabinet)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-primary hover:underline truncate"
                        >
                          {getTenantUrl(cabinet)}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 ml-4">
                      <span
                        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                          cabinet.status === 'active'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}
                      >
                        {cabinet.status === 'active' ? (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                            Actif
                          </>
                        ) : (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                            Inactif
                          </>
                        )}
                      </span>
                      
                      <span
                        className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${statusBadge.color}`}
                      >
                        {statusBadge.label}
                      </span>
                      
                      <svg
                        className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ✅ Modal Newsletter */}
      {showNewsletterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="border-b p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold mb-1">
                    📧 Envoyer une newsletter
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Envoyez un message aux administrateurs des cabinets déployés
                  </p>
                </div>
                <button
                  onClick={resetNewsletterModal}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  disabled={sendingNewsletter}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-auto flex-1">
              {newsletterResults.length === 0 ? (
                <div className="space-y-6">
                  {/* Template Selector */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Template</label>
                    <select
                      value={selectedTemplate}
                      onChange={(e) => handleTemplateChange(e.target.value as keyof typeof EMAIL_TEMPLATES)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="custom">✏️ Message personnalisé</option>
                      <option value="update">🚀 Mise à jour</option>
                      <option value="maintenance">⚠️ Maintenance</option>
                      <option value="news">📰 Actualités</option>
                    </select>
                  </div>

                  {/* Subject */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Sujet *</label>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Sujet de votre message"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      required
                    />
                  </div>

                  {/* Message Body */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Message *</label>
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Écrivez votre message ici..."
                      rows={12}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring font-mono resize-none"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      💡 Vous pouvez utiliser Markdown pour la mise en forme
                    </p>
                  </div>

                  {/* Cabinet Selection */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">Destinataires</label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="rounded"
                        />
                        <span>Sélectionner tout</span>
                      </label>
                    </div>
                    
                    <div className="rounded-lg border bg-muted/30 p-4 max-h-60 overflow-y-auto space-y-2">
                      {deployedCabinets.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Aucun cabinet déployé
                        </p>
                      ) : (
                        deployedCabinets.map(cabinet => (
                          <label
                            key={cabinet.id}
                            className="flex items-center gap-3 p-3 rounded-md hover:bg-background cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCabinets.includes(cabinet.id)}
                              onChange={() => handleToggleCabinet(cabinet.id)}
                              className="rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{cabinet.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{cabinet.admin_email}</p>
                            </div>
                            <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-1 rounded">
                              Déployé
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {selectedCabinets.length} cabinet(s) sélectionné(s)
                    </p>
                  </div>
                </div>
              ) : (
                /* Results */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">Résultats de l'envoi</h3>
                    <div className="text-sm text-muted-foreground">
                      {newsletterResults.filter(r => r.success).length} / {newsletterResults.length} réussis
                    </div>
                  </div>
                  
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {newsletterResults.map((result, index) => (
                      <div
                        key={index}
                        className={`flex items-start gap-3 p-4 rounded-lg border ${
                          result.success
                            ? 'bg-green-50 dark:bg-green-950/30 border-green-500'
                            : 'bg-red-50 dark:bg-red-950/30 border-red-500'
                        }`}
                      >
                        {result.success ? (
                          <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{result.cabinet}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{result.email}</p>
                          {result.error && (
                            <p className="text-xs text-red-600 dark:text-red-400 mt-2">{result.error}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t p-6 flex gap-3 justify-end bg-muted/30">
              {newsletterResults.length === 0 ? (
                <>
                  <button
                    onClick={resetNewsletterModal}
                    disabled={sendingNewsletter}
                    className="px-4 py-2 rounded-md border border-input bg-background hover:bg-accent transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSendNewsletter}
                    disabled={sendingNewsletter || selectedCabinets.length === 0 || !emailSubject.trim() || !emailBody.trim()}
                    className="px-6 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {sendingNewsletter ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Envoyer à {selectedCabinets.length} cabinet(s)
                      </>
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={resetNewsletterModal}
                  className="px-6 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Fermer
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  return (
    <Page>
      <AdminContent />
    </Page>
  );
}